import type { CurvaDocument } from "./document";
import type { FrameNode, SceneNode } from "./nodes";
import { getActivePageSceneNodes, getAllSceneNodes } from "./document";
import { nodeAncestorChain } from "@/document/operations/nodeWorldTransform";
import { createId } from "@/lib/ids";

export type ScriptEntry = DocumentScript | ScriptFolder;

export interface DocumentScript {
  type: "script";
  id: string;
  name: string;
  source: string;
}

export interface ScriptFolder {
  type: "folder";
  id: string;
  name: string;
  children: ScriptEntry[];
}

export interface ScriptLibrary {
  items: ScriptEntry[];
}

export const DEFAULT_DOCUMENT_SCRIPT = `-- Luau document script.
-- Required: init(). Optional: update(dt), draw().
-- Run needs a context frame: selected top-level frame (or descendant), or the
-- top-level frame at the viewport center when nothing is selected.
-- See docs/scripting-api.md

function init()
  print("Hello from script")
end
`;

export function createEmptyScriptLibrary(): ScriptLibrary {
  return { items: [] };
}

export function createDocumentScript(name = "Script", source = DEFAULT_DOCUMENT_SCRIPT): DocumentScript {
  return {
    type: "script",
    id: createId("script"),
    name,
    source,
  };
}

export function createScriptFolder(name = "Folder"): ScriptFolder {
  return {
    type: "folder",
    id: createId("script-folder"),
    name,
    children: [],
  };
}

export function normalizeScriptLibrary(value: unknown): ScriptLibrary {
  if (!value || typeof value !== "object") {
    return createEmptyScriptLibrary();
  }
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return createEmptyScriptLibrary();
  }
  return { items: record.items.map(normalizeScriptEntry).filter((entry): entry is ScriptEntry => entry !== undefined) };
}

function normalizeScriptEntry(value: unknown): ScriptEntry | undefined {
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const id = typeof record.id === "string" ? record.id : createId("script");
  const name = typeof record.name === "string" && record.name.trim() ? record.name : "Script";
  if (record.type === "folder") {
    const children = Array.isArray(record.children)
      ? record.children.map(normalizeScriptEntry).filter((entry): entry is ScriptEntry => entry !== undefined)
      : [];
    return { type: "folder", id, name, children };
  }
  if (record.type === "script" || typeof record.source === "string") {
    return {
      type: "script",
      id,
      name,
      source: typeof record.source === "string" ? record.source : DEFAULT_DOCUMENT_SCRIPT,
    };
  }
  return undefined;
}

export function normalizeFrameScriptId(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** Flatten library to scripts only (skips folders), stable walk order. */
export function listDocumentScripts(library: ScriptLibrary | undefined): DocumentScript[] {
  if (!library) {
    return [];
  }
  const out: DocumentScript[] = [];
  const walk = (entries: ScriptEntry[]) => {
    for (const entry of entries) {
      if (entry.type === "script") {
        out.push(entry);
      } else {
        walk(entry.children);
      }
    }
  };
  walk(library.items);
  return out;
}

export function listDocumentScriptsSorted(library: ScriptLibrary | undefined): DocumentScript[] {
  return [...listDocumentScripts(library)].sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { numeric: true, sensitivity: "base" }),
  );
}

export function findDocumentScript(library: ScriptLibrary | undefined, scriptId: string): DocumentScript | undefined {
  return listDocumentScripts(library).find(script => script.id === scriptId);
}

export function findScriptEntryPath(
  library: ScriptLibrary,
  entryId: string,
): { parent: ScriptEntry[]; index: number; entry: ScriptEntry } | undefined {
  const search = (entries: ScriptEntry[]): { parent: ScriptEntry[]; index: number; entry: ScriptEntry } | undefined => {
    for (let index = 0; index < entries.length; index += 1) {
      const entry = entries[index]!;
      if (entry.id === entryId) {
        return { parent: entries, index, entry };
      }
      if (entry.type === "folder") {
        const nested = search(entry.children);
        if (nested) {
          return nested;
        }
      }
    }
    return undefined;
  };
  return search(library.items);
}

export function frameHasScript(
  document: CurvaDocument,
  node: SceneNode | undefined,
): node is FrameNode & { scriptId: string } {
  if (node?.type !== "frame" || typeof node.scriptId !== "string") {
    return false;
  }
  return findDocumentScript(document.scripts, node.scriptId) !== undefined;
}

export function framesAttachedToScript(document: CurvaDocument, scriptId: string): FrameNode[] {
  return getAllSceneNodes(document).filter(
    (node): node is FrameNode => node.type === "frame" && node.scriptId === scriptId,
  );
}

export function scriptIsAttached(document: CurvaDocument, scriptId: string): boolean {
  return framesAttachedToScript(document, scriptId).length > 0;
}

export function uniqueScriptName(library: ScriptLibrary, baseName: string): string {
  const existing = new Set(listDocumentScripts(library).map(script => script.name));
  if (!existing.has(baseName)) {
    return baseName;
  }
  let index = 2;
  while (existing.has(`${baseName} ${index}`)) {
    index += 1;
  }
  return `${baseName} ${index}`;
}

export function listScriptFolders(library: ScriptLibrary | undefined): ScriptFolder[] {
  if (!library) {
    return [];
  }
  const out: ScriptFolder[] = [];
  const walk = (entries: ScriptEntry[]) => {
    for (const entry of entries) {
      if (entry.type === "folder") {
        out.push(entry);
        walk(entry.children);
      }
    }
  };
  walk(library.items);
  return out;
}

export function findScriptFolder(library: ScriptLibrary | undefined, folderId: string): ScriptFolder | undefined {
  return listScriptFolders(library).find(folder => folder.id === folderId);
}

export function uniqueFolderName(library: ScriptLibrary, baseName: string): string {
  const existing = new Set(listScriptFolders(library).map(folder => folder.name));
  if (!existing.has(baseName)) {
    return baseName;
  }
  let index = 2;
  while (existing.has(`${baseName} ${index}`)) {
    index += 1;
  }
  return `${baseName} ${index}`;
}

export function findScriptEntry(library: ScriptLibrary | undefined, entryId: string): ScriptEntry | undefined {
  return findScriptEntryPath(library ?? createEmptyScriptLibrary(), entryId)?.entry;
}

export function collectScriptIdsInEntry(entry: ScriptEntry): string[] {
  if (entry.type === "script") {
    return [entry.id];
  }
  return entry.children.flatMap(collectScriptIdsInEntry);
}

export function isScriptEntryDescendant(library: ScriptLibrary, ancestorId: string, maybeDescendantId: string): boolean {
  const ancestor = findScriptEntry(library, ancestorId);
  if (!ancestor || ancestor.type !== "folder") {
    return false;
  }
  const walk = (entries: ScriptEntry[]): boolean => {
    for (const entry of entries) {
      if (entry.id === maybeDescendantId) {
        return true;
      }
      if (entry.type === "folder" && walk(entry.children)) {
        return true;
      }
    }
    return false;
  };
  return walk(ancestor.children);
}

export function parentEntriesForFolder(library: ScriptLibrary, folderId: string | undefined): ScriptEntry[] {
  if (!folderId) {
    return library.items;
  }
  const folder = findScriptFolder(library, folderId);
  return folder?.children ?? library.items;
}

export function cloneScriptEntryWithFreshIds(entry: ScriptEntry): ScriptEntry {
  if (entry.type === "script") {
    return {
      type: "script",
      id: createId("script"),
      name: entry.name,
      source: entry.source,
    };
  }
  return {
    type: "folder",
    id: createId("script-folder"),
    name: entry.name,
    children: entry.children.map(cloneScriptEntryWithFreshIds),
  };
}

export function cloneScriptLibraryWithFreshIds(library: ScriptLibrary | undefined): ScriptLibrary {
  return {
    items: (library?.items ?? []).map(cloneScriptEntryWithFreshIds),
  };
}

/** Page-child frame for `nodeId`, if any. */
export function pageChildFrame(document: CurvaDocument, nodeId: string): FrameNode | undefined {
  for (const page of document.pages) {
    const child = page.children.find(candidate => candidate.id === nodeId);
    if (child?.type === "frame") {
      return child;
    }
  }
  return undefined;
}

/**
 * Resolve the top-level (page-child) frame used as Luau `frame` / Wasm context.
 * Seed may be that frame or any descendant; nested non-frame page children yield undefined.
 */
export function resolveScriptContextFrame(document: CurvaDocument, seedNodeId: string): FrameNode | undefined {
  const asTop = pageChildFrame(document, seedNodeId);
  if (asTop) {
    return asTop;
  }
  const topAncestor = nodeAncestorChain(document, seedNodeId)[0];
  return topAncestor?.type === "frame" ? topAncestor : undefined;
}

export function resolveScriptContextFrameFromSelection(
  document: CurvaDocument,
  selection: {
    selectedNodeIds: readonly string[];
    primarySelectedNodeId?: string | null;
    /** When selection does not yield a context frame, use the top-level frame under this point (paste / mid-viewport heuristic). */
    viewportCenter?: { x: number; y: number };
    activePageId?: string;
  },
): FrameNode | undefined {
  const primary = selection.primarySelectedNodeId;
  const seed =
    primary && selection.selectedNodeIds.includes(primary)
      ? primary
      : selection.selectedNodeIds[0];
  if (seed) {
    const fromSelection = resolveScriptContextFrame(document, seed);
    if (fromSelection) {
      return fromSelection;
    }
  }
  return topLevelFrameAtPoint(document, selection.viewportCenter, selection.activePageId);
}

/** Topmost page-child frame whose AABB contains `point` (layer order reversed). */
function topLevelFrameAtPoint(
  document: CurvaDocument,
  point: { x: number; y: number } | undefined,
  activePageId?: string,
): FrameNode | undefined {
  if (!point) {
    return undefined;
  }
  const hit = [...getActivePageSceneNodes(document, activePageId)]
    .reverse()
    .find(node => node.type === "frame" && pointInBounds(point, node.bounds));
  return hit?.type === "frame" ? hit : undefined;
}

function pointInBounds(
  point: { x: number; y: number },
  bounds: { x: number; y: number; width: number; height: number },
): boolean {
  const x1 = Math.min(bounds.x, bounds.x + bounds.width);
  const x2 = Math.max(bounds.x, bounds.x + bounds.width);
  const y1 = Math.min(bounds.y, bounds.y + bounds.height);
  const y2 = Math.max(bounds.y, bounds.y + bounds.height);
  return point.x >= x1 && point.x <= x2 && point.y >= y1 && point.y <= y2;
}

export function getDocumentScriptSource(document: CurvaDocument, scriptId: string): string | undefined {
  return findDocumentScript(document.scripts, scriptId)?.source;
}
